import clsx from 'clsx';
import {
  CSSProperties,
  DOMAttributes,
  ElementType,
  PropsWithChildren,
} from 'react';

import styles from './Text.module.css';

type Color =
  | 'neutral-4-100'
  | 'neutral-4-70'
  | 'white'
  | 'black'
  | 'neutral-1-70'
  | 'text-primary'
  | 'text-secondary';

type Variant = 'primary' | 'secondary' | 'tertiary' | 'plain';
interface TextProps extends PropsWithChildren {
  className?: string;
  as?: ElementType;

  fontFamily?: CSSProperties['fontFamily'];
  fontSize?: CSSProperties['fontSize'];
  color?: Color;
  fontStyle?: CSSProperties['fontStyle'];
  fontWeight?: CSSProperties['fontWeight'];
  lineHeight?: CSSProperties['lineHeight'];
  letterSpacing?: CSSProperties['letterSpacing'];
  variant?: Variant;
  dangerouslySetInnerHTML?: DOMAttributes<HTMLElement>['dangerouslySetInnerHTML'];
}

type FontVariation = {
  slnt?: number;
  GRAD?: number;
  XOPQ?: number;
  XTRA?: number;
  YOPQ?: number;
  YTAS?: number;
  YTDE?: number;
  YTFI?: number;
  YTLC?: number;
  YTUC?: number;
};

const fontVariationByVariant: Record<Variant, FontVariation> = {
  primary: {
    GRAD: -12,
    XOPQ: 88,
    XTRA: 468,
    YOPQ: 79,
    YTAS: 750,
    YTDE: -203,
    YTFI: 738,
    YTLC: 514,
    YTUC: 712,
    slnt: 0,
  },
  secondary: {
    GRAD: 0,
    XOPQ: 175,
    XTRA: 468,
    YOPQ: 79,
    YTAS: 750,
    YTDE: -203,
    YTFI: 738,
    YTLC: 514,
    YTUC: 712,
    slnt: 0,
  },
  tertiary: {
    GRAD: -12,
    XOPQ: 96,
    XTRA: 468,
    YOPQ: 79,
    YTAS: 750,
    YTDE: -203,
    YTFI: 738,
    YTLC: 514,
    YTUC: 712,
    slnt: 0,
  },
  plain: {},
};

const getFontVariation = (variant: Variant) => {
  const fontVariation = fontVariationByVariant[variant as Variant];

  if (!fontVariation) return '';

  return `'slnt' ${fontVariation.slnt}, 'GRAD' ${fontVariation.GRAD}, 'XOPQ' ${fontVariation.XOPQ}, 'XTRA' ${fontVariation.XTRA}, 'YOPQ' ${fontVariation.YOPQ}, 'YTAS' ${fontVariation.YTAS}, 'YTDE' ${fontVariation.YTDE}, 'YTFI' ${fontVariation.YTFI}, 'YTLC' ${fontVariation.YTLC}, 'YTUC' ${fontVariation.YTUC}`;
};

const colorVariants: Record<Color, string> = {
  'text-primary': 'var(--text-primary)',
  'neutral-4-100': 'var(--neutral-4-100)',
  'neutral-4-70': 'var(--neutral-4-70)',
  white: '#fff',
  black: '#000',
  'neutral-1-70': 'var(--neutral-1-70)',
  'text-secondary': 'var(--text-secondary)',
};

export const Text = ({
  children,
  className,
  as: Component = 'span',
  fontFamily,
  fontSize,
  color = 'text-primary',
  fontStyle,
  fontWeight,
  lineHeight,
  letterSpacing,
  variant = 'plain',
  ...props
}: TextProps) => {
  const fontVariationSettings = getFontVariation(variant);

  return (
    <Component
      className={clsx(styles.text, className)}
      style={{
        '--font-family': fontFamily,
        '--font-variation-settings': fontVariationSettings || undefined,
        '--font-size': fontSize ? `${fontSize}px` : undefined,
        '--color': `${colorVariants[color ?? 'white']}`,
        '--font-style': fontStyle,
        '--font-weight': fontWeight,
        '--line-height':
          typeof lineHeight === 'number' ? `${lineHeight}px` : lineHeight,
        '--letter-spacing': letterSpacing ? `${letterSpacing}px` : undefined,
      }}
      {...props}
    >
      {children}
    </Component>
  );
};
